
import img0 from './Images/EditEncryptionMapppingsPage.png';
import img1 from './Images/EncryptionSettingChangePage.png';
import img2 from './Images/EncryptionToolSettings.png';
import img3 from './Images/DatabaseEncryptionSettingChangePage.png';
  
const content = `# Apply Encryption Setting Changes

This document is to guide how to use the OnData Encryption Tool to convert an unencrypted database into an encrypted database, or update an encrypted database in 
production environment. The updates could include:

- Schema changes
  - Fields added
  - Fields removed
  - Fields changed for any of the following attributes
    - Type
    - Precision
    - Scale
    - IsIdentity
    - IsComputed
    - DefaultValue
    - Position
    - MaxLength
- Encrypting rule changes. ***Note:*** To facilitate user selection, one encryptor may be used to support multiple encryption rules. For example, \`Title\` and \`Name\` use the same encryptor. If an encryption rule has changed but the encryptor remains the same, the database does not need to be changed.
- Masking rule changes
  - Masking rule type
  - Masking rule value
- Encryption key changes
- Or any combination of the above

As the encrypted database in production environment would require the OnData Drivers, this feature is not available to customers who do not subscribe to the \`OnData Driver\` feature.

## Convert an Unencrypted Database into an Encrypted Database

To use the OnData Driver related features in a production environment, you must convert the unencrypted database to an encrypted database. The steps are detailed below.

1. Upload the database schema to the OnData Portal. You can either use the OnData Encryption Tool to do that (this is the preferred way, you can follow the steps in \`Section 1. Run GUI Version of OnData Encryption Tool on Windows\` of the instructions in 
[**Use OnData Encryption Tool**](reference_doc#securedb/ondata_encryption/use_ondata_encryption_tool)), or follow the steps below:  
a. Create a backup job for the production database and assign an appropriate encryption key to it.  
b. Perform a schema-only backup of the database. Please refer to \`SecureDB / Database Backup\`.  
  
2. Navigate to \`Services\` -> \`Encryption\` -> \`Encryption Mappings\` -> \`Edit Encryption Mappings\` and select a desired database to configure the data classifications, encryption rules, and masking rules.
  
![Edit Encryption Mappings page](${ img0 })
  
3. Navigate to \`Services\` -> \`Encryption\` -> \`Encryption Setting Change\` page to review the encryption setting changes. Select the \`Lock\` button to start the process of applying the encryption settings.
  
![Encryption Setting Change](${ img1 })
  
4. (Optional) If you want to test the encryption process and the OnData Driver features in a test environment, please follow the steps below:
   - Backup the test database. You may need to restore the test database later in case of any errors or failures. The test database's name must be same with the production environment.
   - Run the OnData Encryption Tool
     - Select the \`Apply Encryption Setting Changes\` option from the \`Operating Mode\` dropdown list
     - Select the \`Non-production\` option from the \`Operating Environment\` dropdown list 
     - If the tool runs successfully, the encryption settings have been applied to the database.
     - If the tool fails to run, you can choose to restore the database and retry. Or you can choose to restore the database and rollback the process.
   - Set the \`OperatingEnvironment\` for the OnData Driver in the OnDataDriver.config file to \`NonProduction\` and test your applications with the encrypted database and the OnData Driver.
   - (Optional) If you need to adjust the database schema or encryption settings, you can go to the \`Encryption Setting Change\` page and select \`Unlock\`. Then you can make the desired adjustments, lock the changes, and run the test again.
  
![Encryption Tool Settingse](${ img2 })
  
5. To apply the encryption settings to the production environment, you may follow steps below:
    - Backup the production database. You may need to restore the production database later in case any errors or failures.
    - Run the OnData Encryption Tool
      - Select the \`Apply Encryption Setting Changes\` option from the \`Operating Mode\` dropdown list
      - Select the \`Production\` option from the \`Operating Environment\` dropdown list 
      - If the tool runs successfully, the database has been converted to an encrypted database. You may validate that your applications can work well with the encrypted 
      database using the OnData Driver. Set the \`OperatingEnvironment\` for the OnData Driver in the OnDataDriver.config file to \`Production\` and test your applications with 
      the encrypted database and the OnData Driver. Once validated, you can go to the \`Encryption Setting Change\` page and select \`Finalize\` to complete the encryption process;
      - If the tool fails to run, you may restore the production database and retry the encryption process. Or you may restore the production database, and go to 
      the \`Encryption Setting Change\` page and select \`Rollback\` to stay with the unencrypted database.

## Apply Encryption Setting Changes to an Encrypted Database

To apply any encryption setting changes to an encrypted database in the production environment, please follow the steps below.

1. If there is any change to the database schema, perform a schema-only backup for the database first.
2. If desired, update the encryption key and/or the assignment of the encryption rules at the OnData Portal.
3. Navigate to \`Services\` -> \`Encryption\` -> \`Encryption Setting Change\` page to review the encryption setting changes. Select the \`Lock\` button to start the process of applying the encryption setting changes.
  
![Database Encryption Setting Change](${ img3 })
  
4. (Optional) If you want to test the update process in a test environment, please follow the steps below:
   - Backup the test database. You may need to restore the test database later in case of any errors or failures. The test database's name must be same with the production environment.
   - Run the OnData Encryption Tool
     - Select the \`Apply Encryption Setting Changes\` option from the \`Operating Mode\` dropdown list
     - Select the \`Non-production\` option from the \`Operating Environment\` dropdown list 
     - If the tool runs successfully, the encryption setting changes have been applied to the database.
     - If the tool fails to run, you can choose to restore the database and retry. Or you can choose to restore the database and rollback the process.
   - Set the \`OperatingEnvironment\` for the OnData Driver in the OnDataDriver.config file to \`NonProduction\` and test your applications with the encrypted database and the OnData Driver.
   - (Optional) If you need to adjust the database schema or encryption settings, you can go to the \`Encryption Setting Change\` page and select \`Unlock\`. Then you can make the desired adjustments, 
   lock the changes, and run the test again.
5. To apply the encryption setting changes to the production environment, you may follow steps below:
   - Backup the production database. You may need to restore the production database later in case any errors or failures.
   - Run the OnData Encryption Tool
      - Select the \`Apply Encryption Setting Changes\` option from the \`Operating Mode\` dropdown list
      - Select the \`Production\` option from the \`Operating Environment\` dropdown list 
      - If the tool runs successfully, the database encryption settings have been applied. You may validate that your applications can work well with the updated database using the OnData Driver. 
      Set the \`OperatingEnvironment\` for the OnData Driver in the OnDataDriver.config file to \`Production\` and test your applications with 
      the encrypted database and the OnData Driver. Once validated, you can go to the \`Encryption Setting Change\` page and select \`Finalize\` to complete the update process;
      - If the tool fails to run, you may restore the production database and retry the update process. Or you may restore the production database, and go to 
      the \`Encryption Setting Change\` page and select \`Rollback\` to stay with the previous encryption settings.
`
  
export default content;
